/**
 * Shared stage layout — pure constants, no React, no DOM.
 *
 * Extracted from `features/library/Stage.tsx` (Plan 13, Task 5.1, D8) so the
 * decorative library `Stage` and the synced editor `LiveStage` draw the band
 * from one source of truth. The headless clip-frame renderer
 * (`@studio/domain` → `renderStageFrameSvg`) keeps its own copy of the same
 * numbers because it cannot import from `studio-web`; both are anchored to the
 * values here to minimize divergence (spec R1).
 *
 * Keeping this module free of React/`tailwindcss` lets it be imported by hooks
 * (`useStageEnergy`) and tested without a DOM.
 */

import type { LightColor, SlotRole } from "../../infra/api.js";

/** Hex value for each configurable stage `LightColor`. */
export const LIGHT_HEX: Record<LightColor, string> = {
  warm: "#ff8a3a",
  cool: "#5ab0c8",
  magenta: "#e040b8",
  green: "#7ac850",
  white: "#fff8dc",
  amber: "#f0c84a",
};

/** Where each band slot stands on the stage (percent of the stage box). */
export const POSITIONS: Record<
  SlotRole,
  { left: string; top: string; zIndex: number; scale?: number }
> = {
  drums: { left: "50%", top: "20%", zIndex: 1, scale: 0.78 },
  keys: { left: "22%", top: "26%", zIndex: 1, scale: 0.82 },
  vocal: { left: "50%", top: "62%", zIndex: 3 },
  guitar: { left: "22%", top: "62%", zIndex: 2 },
  bass: { left: "78%", top: "62%", zIndex: 2 },
};

/**
 * Stems separated by Demucs. The envelope event keys its series by these
 * names; `useStageEnergy` reads them back.
 */
export type StemName = "drums" | "bass" | "vocals" | "other";
export const STEM_NAMES: readonly StemName[] = ["drums", "bass", "vocals", "other"];

/**
 * Maps a band slot to the stem whose energy drives its avatar (spec §8.1).
 * `guitar` and `keys` share the `other` stem — Demucs has no dedicated bucket
 * for them.
 */
export const SLOT_TO_STEM: Record<SlotRole, StemName> = {
  drums: "drums",
  bass: "bass",
  vocal: "vocals",
  guitar: "other",
  keys: "other",
};

/**
 * Maps a DNA `mood` string to a stage `LightColor` (spec §8.1). The DNA mood
 * is free-form; unknown moods fall back to `warm`. Lowercased before lookup.
 */
const MOOD_TO_LIGHT: Record<string, LightColor> = {
  energetic: "magenta",
  upbeat: "magenta",
  happy: "amber",
  bright: "amber",
  calm: "cool",
  chill: "cool",
  mellow: "cool",
  sad: "cool",
  dark: "warm",
  moody: "warm",
  aggressive: "magenta",
  dreamy: "cool",
  warm: "amber",
  epic: "magenta",
};

/** Resolve a DNA mood to a spotlight color, defaulting to `warm`. */
export function moodToLightColor(mood: string | null | undefined): LightColor {
  if (!mood) return "warm";
  return MOOD_TO_LIGHT[mood.trim().toLowerCase()] ?? "warm";
}
